import React from "react";
import useSeo from "../hooks/useSeo";
import { Link } from "react-router-dom";
import { ShieldCheck, Users, Heart, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useJobModal } from "../context/JobModalContext";
import { ABOUT_IMAGE } from "../mock";

export default function About() {
  const { openJobModal } = useJobModal();
  useSeo("About SimplySitter — Trusted Family Care", "SimplySitter connects US families with vetted, background-checked nannies, babysitters, housekeepers and senior caregivers they can trust.");

  return (
    <div>
      <Header />
      <section className="hero-gradient">
        <div className="max-w-7xl mx-auto px-5 pt-14 pb-16 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <h1 className="font-display text-[2.6rem] leading-[1.05] sm:text-6xl text-[#1f1a1b]">Care that feels like family</h1>
            <p className="mt-5 text-[18px] leading-relaxed text-[#5a5254] max-w-xl">
              Behind every job posted on SimplySitter is a parent hoping to feel calm again &mdash; and a caregiver ready to make a real difference in a family's day. We built SimplySitter so that trust comes first, and finding the right person feels less like a gamble and more like a warm introduction.
            </p>
          </div>
          <div>
            <img src={ABOUT_IMAGE} alt="Caregiver with a child at home" className="w-full h-[420px] object-cover rounded-[28px] shadow-[0_30px_70px_-25px_rgba(31,26,27,0.4)]" />
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-5 py-16 space-y-8">
          <div>
            <h2 className="font-display text-2xl text-[#1f1a1b]">Our story</h2>
            <p className="text-[16px] text-[#5a5254] mt-3 leading-relaxed">
              SimplySitter began with a feeling every parent knows: the knot in your stomach when you leave your child, your home, or an aging parent in someone else's hands. Finding care shouldn't mean endless scrolling, uncertain backgrounds and sleepless nights &mdash; it should feel safe, personal and human. So we built a place where families simply say what they need, and hear only from caregivers who have already earned our trust.
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#1f1a1b]">What makes us different</h2>
            <p className="text-[16px] text-[#5a5254] mt-3 leading-relaxed">
              Every caregiver on SimplySitter verifies their identity, is personally interviewed and passes background and reference checks before they can apply to a job. We pre-select in advance so families never have to guess. There's no placement commission — just a transparent membership, with the first month free.
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#1f1a1b]">Our values</h2>
            <ul className="grid sm:grid-cols-2 gap-4 mt-4">
              {[[ShieldCheck, "Safety first", "Rigorous vetting on every caregiver, every time."], [Heart, "Genuine care", "We treat families and caregivers with warmth and respect."], [Users, "Real people", "Personal support from a team that actually answers."], [MapPin, "Local focus", "Caregivers matched to your city and neighborhood."]].map(([Icon, t, d]) => (
                <li key={t} className="rounded-2xl border border-black/[0.07] p-5">
                  <div className="w-10 h-10 rounded-xl bg-[#f7e4e6] flex items-center justify-center"><Icon className="w-5 h-5 text-[#7c2233]" strokeWidth={1.7} /></div>
                  <h3 className="font-display text-lg text-[#1f1a1b] mt-3">{t}</h3>
                  <p className="text-[15px] text-[#5a5254] mt-1">{d}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-[#fdfaf7]">
        <div className="max-w-7xl mx-auto px-5 py-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          {[["8,500+", "families served"], ["100%", "vetted caregivers"], ["15+", "cities across the US"]].map(([v, l]) => (
            <div key={l} className="rounded-2xl border border-black/[0.07] bg-white px-6 py-7 text-center">
              <div className="font-display text-3xl text-[#1f1a1b]">{v}</div>
              <div className="text-sm text-[#8a8285] mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 pb-16">
          <div className="rounded-3xl bg-[#7c2233] px-8 py-12 text-center">
            <h2 className="font-display text-3xl text-white">Ready to find trusted care?</h2>
            <p className="text-white/80 mt-3">1 month free, then $15.99/month. Cancel anytime.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button onClick={() => openJobModal({})} className="rounded-xl bg-white text-[#7c2233] font-semibold px-7 py-3.5 hover:bg-[#f7e4e6] transition-colors">Post a job free</button>
              <Link to="/for-caregivers" className="rounded-xl border border-white/25 text-white font-medium px-7 py-3.5 hover:bg-white/10 transition-colors">Become a caregiver</Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
